import { ProductMetadataFieldKey } from './product'

export enum ProductUpdateEventKey {
  PRODUCT_METADATA_UPDATED = 'PRODUCT_METADATA_UPDATED',
  PRODUCT_SOURCE_ADDED = 'PRODUCT_SOURCE_ADDED',
  PRODUCT_SOURCE_DELETED = 'PRODUCT_SOURCE_DELETED',
  PRODUCT_MANUFACTURER_ASSIGNED = 'PRODUCT_MANUFACTURER_ASSIGNED',
  PRODUCT_MANUFACTURER_UNASSIGNED = 'PRODUCT_MANUFACTURER_UNASSIGNED',
}

export type ProductUpdateEvent =
  | {
      eventKey: ProductUpdateEventKey.PRODUCT_METADATA_UPDATED
      params: {
        updatedFields: ProductMetadataFieldKey[]
      }
    }
  | {
      eventKey: ProductUpdateEventKey.PRODUCT_SOURCE_ADDED
      params: {
        sourceId: string
        sourceName: string
      }
    }
  | {
      eventKey: ProductUpdateEventKey.PRODUCT_SOURCE_DELETED
      params: {
        sourceId: string
        sourceName: string
      }
    }
  | {
      eventKey: ProductUpdateEventKey.PRODUCT_MANUFACTURER_ASSIGNED
      params: {
        manufacturerId: string
        manufacturerName: string
      }
    }
  | {
      eventKey: ProductUpdateEventKey.PRODUCT_MANUFACTURER_UNASSIGNED
      params: {
        manufacturerId: string
        manufacturerName: string
      }
    }

export type ProductUpdateSystemMessageContentJson = {
  eventKey: ProductUpdateEvent['eventKey']
  params: ProductUpdateEvent['params']
  actorType: 'user'
  actorUserId: string
  resourceType: 'product'
  resourceId: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Catalog Save/Ignore dismiss — model context only, not a product write. */
export function isDismissedCatalogRecommendationResolution(
  contentJson: unknown
): boolean {
  if (!isRecord(contentJson)) {
    return false
  }
  const params = contentJson.params
  if (!isRecord(params)) {
    return false
  }
  return params.resolution === 'dismissed'
}

export function parseProductUpdateEvent(
  contentJson: unknown
): ProductUpdateEvent | null {
  if (!isRecord(contentJson) || typeof contentJson.eventKey !== 'string') {
    return null
  }
  const params = contentJson.params
  if (!isRecord(params)) {
    return null
  }

  if (contentJson.eventKey === ProductUpdateEventKey.PRODUCT_METADATA_UPDATED) {
    if (!Array.isArray(params.updatedFields)) {
      return null
    }
    const updatedFields = params.updatedFields as ProductMetadataFieldKey[]
    if (updatedFields.length === 0) {
      return null
    }
    return {
      eventKey: ProductUpdateEventKey.PRODUCT_METADATA_UPDATED,
      params: { updatedFields },
    }
  }

  if (
    contentJson.eventKey === ProductUpdateEventKey.PRODUCT_SOURCE_ADDED ||
    contentJson.eventKey === ProductUpdateEventKey.PRODUCT_SOURCE_DELETED
  ) {
    if (
      typeof params.sourceId !== 'string' ||
      typeof params.sourceName !== 'string'
    ) {
      return null
    }
    return {
      eventKey: contentJson.eventKey,
      params: {
        sourceId: params.sourceId,
        sourceName: params.sourceName,
      },
    }
  }

  if (
    contentJson.eventKey ===
      ProductUpdateEventKey.PRODUCT_MANUFACTURER_ASSIGNED ||
    contentJson.eventKey ===
      ProductUpdateEventKey.PRODUCT_MANUFACTURER_UNASSIGNED
  ) {
    if (
      typeof params.manufacturerId !== 'string' ||
      typeof params.manufacturerName !== 'string'
    ) {
      return null
    }
    return {
      eventKey: contentJson.eventKey,
      params: {
        manufacturerId: params.manufacturerId,
        manufacturerName: params.manufacturerName,
      },
    }
  }

  return null
}

export function parseProductUpdateSystemMessageContentJson(
  contentJson: unknown
): ProductUpdateSystemMessageContentJson | null {
  const event = parseProductUpdateEvent(contentJson)
  if (event === null || !isRecord(contentJson)) {
    return null
  }
  if (
    contentJson.actorType !== 'user' ||
    typeof contentJson.actorUserId !== 'string' ||
    contentJson.resourceType !== 'product' ||
    typeof contentJson.resourceId !== 'string'
  ) {
    return null
  }

  return {
    eventKey: event.eventKey,
    params: event.params,
    actorType: 'user',
    actorUserId: contentJson.actorUserId,
    resourceType: 'product',
    resourceId: contentJson.resourceId,
  }
}
